/**
 * StudioChatDock — the chat's chrome: conversations, model, money.
 *
 * The Studio's design agent used to be one anonymous scrollback in a
 * drawer. This wraps StudioChatPanel in the header a real chat product
 * has (ChatGPT / Palmier) — ONE row (spec §4), because three rows of
 * chrome above a 360 px column is more chrome than conversation:
 *
 *   ┌────────────────────────────────────────────┐
 *   │ [ Thread ▾ ] [＋] [$12.40] [⋯]              │
 *   └────────────────────────────────────────────┘
 *
 * The `⋯` menu holds the two settings nobody changes twice: which model
 * answers, and whether the chat is docked or floating.
 *
 * The dock OWNS the thread id and the model; the panel just renders the
 * conversation it is handed. Both persist in localStorage — thread per
 * clip (two clips open in two tabs keep their own), model globally (it's
 * a spending preference, not a property of a video).
 *
 * THE FIRST THREAD OF A CLIP IS "default", not a uuid. Every chat row
 * written before threads existed carries no threadId and reads back as
 * "default" — so a clip with history shows that history as its first
 * conversation instead of looking freshly wiped.
 *
 * The credit chip is the one place the user sees whose key is paying:
 * "Credit $12.40" on platform credits (amber + "Ask for credit" at or below
 * zero — we never block a render for money, and an empty meter on day one is
 * not a mistake the user made), "Own key ····1234" when they brought their
 * own. It re-reads after every assistant turn, and clicking it opens the AI
 * section of Settings.
 */
import { MessageSquarePlus, MoreHorizontal, PanelLeft, PanelRight } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { modelOptionLabel } from "../lib/modelCopy";
import {
  getAiSettings,
  listStudioChatThreads,
  type AiSettingsInfo,
  type ChatThreadInfo,
} from "../api";
import {
  DEFAULT_THREAD_ID,
  formatUsd,
  loadChatModel,
  loadSelectedThread,
  newThreadId,
  resolveChatModel,
  saveChatModel,
  saveSelectedThread,
  type ChatDockMode,
} from "../lib/chatThreads";
import { StudioChatPanel, type StudioChatPanelProps } from "./StudioChatPanel";
import { Select } from "./ui/Select";

const ACCENT = "#FFD500";

/** Everything the panel needs that the DOCK doesn't own. */
export type DockPanelProps = Omit<
  StudioChatPanelProps,
  "clipName" | "threadId" | "model" | "header" | "onAssistantTurn" | "height"
>;

export interface StudioChatDockProps {
  clipName: string;
  panel: DockPanelProps;
  /** Where the dock currently lives — shown in the header toggle. */
  mode: ChatDockMode;
  onModeChange: (mode: ChatDockMode) => void;
  /** False hides the Dock-left/Floating toggle (mobile, where a 360 px
   *  column next to a canvas doesn't exist and "dock left" would just
   *  make the chat vanish). */
  canChangeMode?: boolean;
  /** Settings route (balance chip target). Omitted ⇒ the chip is inert text. */
  onOpenSettings?: () => void;
  /** Raised when an assistant reply lands — the host turns it into the
   *  collapsed rail's unread dot. */
  onAssistantTurn?: () => void;
  height?: number | string;
}

const chipStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  padding: "5px 9px",
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,.10)",
  background: "rgba(255,255,255,.04)",
  color: "var(--ql-fg-muted)",
  fontFamily: "inherit",
  fontSize: 11,
  fontWeight: 700,
  whiteSpace: "nowrap",
};

/** A thread row for the conversation Select: the server's list, plus the thread the
 *  operator just created (which has no rows yet, so the server can't know
 *  about it). */
function mergeThreads(
  server: ChatThreadInfo[],
  selectedId: string,
): ChatThreadInfo[] {
  if (server.some((t) => t.id === selectedId)) return server;
  return [
    { id: selectedId, title: "New chat", updatedAt: "", count: 0 },
    ...server,
  ];
}

function threadLabel(t: ChatThreadInfo): string {
  const title = t.title?.trim() || "New chat";
  return t.count > 0 ? `${title} · ${t.count}` : title;
}

export function StudioChatDock({
  clipName,
  panel,
  mode,
  onModeChange,
  canChangeMode = true,
  onOpenSettings,
  onAssistantTurn,
  height = "100%",
}: StudioChatDockProps): React.JSX.Element {
  const [threads, setThreads] = useState<ChatThreadInfo[]>([]);
  const [threadId, setThreadId] = useState<string>(
    () => loadSelectedThread(clipName) ?? DEFAULT_THREAD_ID,
  );
  const [settings, setSettings] = useState<AiSettingsInfo | null>(null);
  const [model, setModel] = useState<string>("");
  // One slot PER fetch, each cleared by its own success: a shared slot let a
  // single transient failure pin a red line for the rest of the session.
  const [threadsError, setThreadsError] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  /** The operator's pick wins over the deployment default once he makes
   *  one — without this the settings refetch would keep re-resolving. */
  const modelPickedRef = useRef<boolean>(loadChatModel() !== null);
  /** The ⋯ menu: model + where the chat lives. Closed by Escape or a click
   *  outside, like every other popover in the app. */
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      setMenuOpen(false);
      // Escape must not strand the keyboard at the end of the document —
      // the trigger is where the user was.
      menuBtnRef.current?.focus();
    };
    const onClick = (e: MouseEvent): void => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onClick);
    };
  }, [menuOpen]);

  // ---- threads: load for this clip, re-select what was last open ----
  const refreshThreads = useCallback(
    (opts: { pickFirst?: boolean } = {}) => {
      let alive = true;
      listStudioChatThreads(clipName)
        .then((list) => {
          if (!alive) return;
          setThreads(list);
          setThreadsError(null);
          if (opts.pickFirst) {
            // Stored pick wins (it may be a brand-new, empty thread); else
            // the most recently touched conversation; else "default".
            const stored = loadSelectedThread(clipName);
            setThreadId(stored ?? list[0]?.id ?? DEFAULT_THREAD_ID);
          }
        })
        .catch((err: unknown) => {
          if (alive) {
            setThreadsError(
              `Couldn't load conversations: ${err instanceof Error ? err.message : String(err)}`,
            );
          }
        });
      return () => {
        alive = false;
      };
    },
    [clipName],
  );

  useEffect(() => {
    setThreads([]);
    setThreadsError(null);
    setThreadId(loadSelectedThread(clipName) ?? DEFAULT_THREAD_ID);
    return refreshThreads({ pickFirst: true });
  }, [clipName, refreshThreads]);

  // ---- provider settings: model catalog + the balance chip ----
  const refreshSettings = useCallback(() => {
    let alive = true;
    getAiSettings()
      .then((s) => {
        if (!alive) return;
        setSettings(s);
        setSettingsError(null);
        setModel((cur) =>
          resolveChatModel({
            stored: modelPickedRef.current ? loadChatModel() : cur || null,
            models: s.models,
            agentModel: s.designAgent.model,
            defaultModel: s.defaultModel,
          }),
        );
      })
      .catch((err: unknown) => {
        if (alive) {
          setSettingsError(
            `Couldn't read AI settings: ${err instanceof Error ? err.message : String(err)}`,
          );
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => refreshSettings(), [refreshSettings]);

  const selectThread = useCallback(
    (id: string) => {
      setThreadId(id);
      saveSelectedThread(clipName, id);
    },
    [clipName],
  );

  const newChat = useCallback(() => {
    // A clip that has never been chatted about starts at "default" so its
    // pre-threads history (if any) is the first conversation, not an orphan.
    const id = threads.length === 0 ? DEFAULT_THREAD_ID : newThreadId();
    selectThread(id);
  }, [threads.length, selectThread]);

  // "New chat" on a conversation that has nothing in it would stack a second
  // empty thread on the first — the button does nothing there, and says so.
  const currentIsEmpty = (threads.find((t) => t.id === threadId)?.count ?? 0) === 0;

  const handleAssistantTurn = useCallback(() => {
    refreshThreads();
    refreshSettings();
    onAssistantTurn?.();
  }, [refreshThreads, refreshSettings, onAssistantTurn]);

  const rows = mergeThreads(threads, threadId);
  const provider = settings?.provider;
  const balance = settings?.credits.balanceUsd ?? 0;
  // "Low" covers a fresh deployment (nothing topped up yet) as well as an
  // overdrawn one — both need the same one action, and neither is an error.
  const low = provider?.mode === "platform" && balance <= 0;

  const header = (
    <div style={{ display: "grid", gap: 7, paddingBottom: 10 }}>
      {/* ONE row: the conversation, a way to start another, what it costs,
          and the ⋯ that hides everything else. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <Select
          value={threadId}
          onChange={selectThread}
          aria-label="Conversation"
          title="Switch conversation"
          size="sm"
          style={{ flex: 1 }}
          options={rows.map((t) => ({ value: t.id, label: threadLabel(t) }))}
        />
        <button
          type="button"
          onClick={newChat}
          disabled={currentIsEmpty}
          aria-label="New chat"
          title={currentIsEmpty ? "This conversation is already empty." : "Start a new conversation about this clip"}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: 9,
            border: "1px solid rgba(255,213,0,.35)",
            background: "rgba(255,213,0,.08)",
            color: ACCENT,
            fontFamily: "inherit",
            cursor: currentIsEmpty ? "default" : "pointer",
            opacity: currentIsEmpty ? 0.45 : 1,
            flexShrink: 0,
            padding: 0,
          }}
        >
          <MessageSquarePlus size={13} />
        </button>
        {provider && (
          <button
            type="button"
            onClick={() => onOpenSettings?.()}
            title={
              provider.mode === "own"
                ? "Your own Anthropic key is paying — open Settings"
                : "Platform credit pays for the design agent and renders."
            }
            style={{
              ...chipStyle,
              cursor: onOpenSettings ? "pointer" : "default",
              // Amber, not red: an empty meter on day one is not an error the
              // user made, and red reads as "you broke something".
              ...(low
                ? {
                    color: "var(--ql-warning)",
                    borderColor: "rgba(251,191,36,.40)",
                    background: "rgba(251,191,36,.08)",
                  }
                : {}),
            }}
          >
            {provider.mode === "own"
              ? `Own key ····${provider.ownKeyLast4 ?? "????"}`
              : low
                ? `Credit ${formatUsd(balance)} · Ask for credit`
                : `Credit ${formatUsd(balance)}`}
          </button>
        )}
        <div ref={menuRef} style={{ position: "relative", flexShrink: 0 }}>
          <button
            ref={menuBtnRef}
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            // Not `haspopup="menu"`: this popover holds a Select and a
            // toggle, not a list of commands — announcing a menu would
            // promise arrow-key navigation that isn't there.
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
            aria-label="Chat options"
            title="Model · where the chat lives"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 9,
              border: "1px solid rgba(255,255,255,.10)",
              background: "rgba(255,255,255,.04)",
              color: "var(--ql-fg-muted)",
              fontFamily: "inherit",
              cursor: "pointer",
              padding: 0,
            }}
          >
            <MoreHorizontal size={14} />
          </button>
          {menuOpen && (
            <div
              role="group"
              aria-label="Chat options"
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                zIndex: 40,
                width: 230,
                display: "grid",
                gap: 8,
                padding: 10,
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,.12)",
                background: "var(--ql-bg-raised, #101319)",
                boxShadow: "0 14px 40px rgba(0,0,0,.5)",
              }}
            >
              {/* A div, not a <label>: a label forwards clicks on its text to
                  the button inside, which would toggle the list twice. */}
              <div style={{ display: "grid", gap: 4 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--ql-fg-subtle)" }}>
                  Model
                </span>
                <Select
                  value={model}
                  onChange={(v) => {
                    modelPickedRef.current = true;
                    setModel(v);
                    saveChatModel(v);
                    // Picked = done; leaving the popover open over the
                    // scrollback just hides the conversation. The ⋯ button
                    // takes the focus back so the keyboard is not stranded.
                    setMenuOpen(false);
                    menuBtnRef.current?.focus();
                  }}
                  aria-label="Model"
                  title="Which model answers this chat"
                  disabled={!settings}
                  size="sm"
                  placeholder="Loading models…"
                  // Models that cannot drive the editor stay listed but
                  // unpickable — the label says why (`modelOptionLabel`).
                  options={(settings?.models ?? []).map((m) => ({
                    value: m.id,
                    label: modelOptionLabel(m),
                    disabled: !m.forcedToolUse,
                  }))}
                />
              </div>
              {canChangeMode && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onModeChange(mode === "left" ? "drawer" : "left");
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "8px 9px",
                    borderRadius: 9,
                    border: "1px solid rgba(255,255,255,.10)",
                    background: "rgba(255,255,255,.03)",
                    color: "var(--ql-fg)",
                    fontFamily: "inherit",
                    fontSize: 11.5,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  {mode === "left" ? <PanelRight size={12} /> : <PanelLeft size={12} />}
                  {mode === "left" ? "Floating" : "Dock left"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Keyed by WHICH error this is, never by its text: two failures that
          happen to word themselves the same way collide on a text key. */}
      {([["threads", threadsError], ["settings", settingsError]] as const)
        .filter(([, msg]) => !!msg)
        .map(([which, msg]) => (
          <div key={which} style={{ fontSize: 10.5, lineHeight: 1.45, color: "var(--ql-danger)" }}>
            {msg}
          </div>
        ))}
    </div>
  );

  return (
    // The dock's root carries the tour anchor; the panel fills it exactly
    // (same `height` contract as before, one wrapper deeper).
    <div data-tour="studio-chat" style={{ height, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <StudioChatPanel
        {...panel}
        clipName={clipName}
        threadId={threadId}
        {...(model ? { model } : {})}
        header={header}
        onAssistantTurn={handleAssistantTurn}
        height="100%"
      />
    </div>
  );
}
